/**
 * Profile's standard look (profile-web.html Mockup 10, the prototype's Profile):
 * one centered column, each section's heading above its rows; My info turns
 * into its fields where it stands; Remove card asks in a centered window.
 */
import { useEffect, useId, useRef } from 'react';

import { partsOf, pieceRoot } from '../../contract/runtime';
import account from '../../styles/account.module.css';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { contract, type ProfileField } from './contract';
import css from './Profile.module.css';
import type { ProfileRuntime } from './useProfile';

const part = partsOf(contract);

const INPUT: Record<ProfileField, { type: string; inputMode?: 'email' | 'tel' }> = {
  firstName: { type: 'text' },
  lastName: { type: 'text' },
  email: { type: 'email', inputMode: 'email' },
  phone: { type: 'tel', inputMode: 'tel' },
};

function MyInfo({ state, actions }: ProfileRuntime) {
  const edit = state.edit;
  const box = useRef<HTMLDivElement>(null);
  const editing = edit !== null;
  // Editing starts in the field that was clicked, the caret at its end.
  useEffect(() => {
    if (!editing) return;
    const input = box.current?.querySelector<HTMLInputElement>(`[data-field="${edit.active}"]`);
    if (!input) return;
    input.focus();
    try {
      input.setSelectionRange(input.value.length, input.value.length);
    } catch {
      /* email inputs refuse a selection */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when editing starts
  }, [editing]);

  if (!edit) {
    return (
      <div className={css.list}>
        {state.info.map((r) => (
          <button key={r.key} type="button" className={css.row} onClick={() => actions.startEdit(r.key)} {...part('infoRow')}>
            <span className={css.k}>{r.label}</span>
            <span className={`${css.v} ${r.value ? '' : css.unset}`}>{r.value || 'Not set'}</span>
            <span className={css.act}>Edit</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div ref={box}>
      <div className={css.list}>
        {state.info.map((r) => {
          const error = r.key === 'email' ? edit.emailError : null;
          return (
            <label key={r.key} className={`${css.field} ${edit.active === r.key ? css.on : ''}`} {...part('infoField', edit.active === r.key && 'active')}>
              <span className={css.k}>{r.label}</span>
              <span>
                <span className={`${css.input} ${error ? css.bad : ''}`}>
                  <input
                    data-field={r.key}
                    type={INPUT[r.key].type}
                    inputMode={INPUT[r.key].inputMode}
                    value={edit.values[r.key]}
                    maxLength={edit.maxLength[r.key]}
                    autoComplete="off"
                    readOnly={edit.saving}
                    aria-invalid={error ? 'true' : undefined}
                    onChange={(e) => actions.setField(r.key, e.target.value)}
                    onFocus={() => actions.focusField(r.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') actions.save();
                      if (e.key === 'Escape' && !edit.saving) actions.cancelEdit();
                    }}
                  />
                </span>
                {error && (
                  <span className={css.fieldErr} {...part('fieldError')}>
                    {error}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      <div className={css.editFoot}>
        {edit.failed && (
          <p className={css.err} role="alert" {...part('saveError')}>
            <Icon name="alert" small />
            Couldn’t save your changes. Please try again.
          </p>
        )}
        <div className={css.btns}>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnPrimary} ${account.btnSm}`}
            disabled={edit.saving || !edit.canSave}
            onClick={actions.save}
            {...part('save', (edit.saving || !edit.canSave) && 'disabled')}
          >
            {edit.saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className={`${shared.btn} ${shared.btnOutline} ${account.btnSm}`} disabled={edit.saving} onClick={actions.cancelEdit} {...part('cancelEdit')}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Cards({ state, actions }: ProfileRuntime) {
  if (state.cardsStatus === 'loading') return <p className={css.quiet}>Loading saved cards…</p>;
  if (state.cardsStatus === 'unavailable') return <p className={css.quiet}>Saved cards are unavailable right now.</p>;
  if (state.cards.length === 0) {
    return (
      <div className={css.empty}>
        <span className={css.orb}>
          <Icon name="card" />
        </span>
        <div>
          <div className={css.emptyT}>No cards saved yet</div>
          <p className={css.emptyS}>To save a card, tick Save at checkout when you pay.</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={css.list}>
        {state.cards.map((c) => (
          <div key={c.id} className={css.card} {...part('card')}>
            {c.badge === 'visa' ? (
              <span className={`${css.brand} ${css.visa}`}>VISA</span>
            ) : c.badge === 'mastercard' ? (
              <span className={`${css.brand} ${css.mc}`} aria-label="Mastercard" />
            ) : (
              <span className={`${css.brand} ${css.other}`}>{c.brand.slice(0, 4).toUpperCase()}</span>
            )}
            <div>
              <div className={css.num}>
                {c.brand}
                {c.last4 && (
                  <>
                    {' '}
                    <span className={css.dots}>····</span> {c.last4}
                  </>
                )}
              </div>
              {c.expires && <div className={css.exp}>{c.expires}</div>}
            </div>
            <button type="button" className={css.remove} onClick={() => actions.askRemoveCard(c.id)} aria-label={c.last4 ? `Remove card ending ${c.last4}` : `Remove ${c.brand}`} {...part('removeCard')}>
              <Icon name="close" small />
              Remove
            </button>
          </div>
        ))}
      </div>
      <p className={css.note}>Saved when you choose to at checkout.</p>
    </>
  );
}

function RemoveWindow({ state, actions }: ProfileRuntime) {
  const titleId = useId();
  const r = state.removing;
  if (!r) return null;
  return (
    <div className={account.scrim} onClick={(e) => e.target === e.currentTarget && !r.busy && actions.keepCard()}>
      <div className={account.dlg} role="dialog" aria-modal="true" aria-labelledby={titleId} {...part('removeWindow')}>
        <div className={account.eyebrow}>Remove card</div>
        <h2 className={account.dlgTitle} id={titleId}>
          Remove this card?
        </h2>
        <p>
          Your <b>{r.card}</b> will be removed. To save it again, tick Save at checkout.
        </p>
        {r.failed && (
          <p className={css.err} role="alert">
            Couldn’t remove your card. Please try again.
          </p>
        )}
        <div className={account.dlgBtns}>
          <button type="button" className={`${shared.btn} ${shared.btnOutline} ${account.btnSm}`} onClick={actions.keepCard} disabled={r.busy}>
            Keep card
          </button>
          <button type="button" className={`${shared.btn} ${account.btnDanger} ${account.btnSm}`} onClick={actions.removeCard} disabled={r.busy}>
            {r.busy ? 'Removing…' : 'Remove card'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProfileView(runtime: ProfileRuntime) {
  const { state } = runtime;
  if (state.status === 'loading') return null;
  if (state.status === 'error') {
    return (
      <div className={`${shared.wrap} ${css.fail}`} {...pieceRoot(contract)}>
        <h1 className={css.h2}>Couldn’t load your profile</h1>
        <p className={css.quiet}>Please try again.</p>
      </div>
    );
  }
  return (
    <div className={shared.wrap} {...pieceRoot(contract)}>
      <div className={css.body} {...part('page')}>
        <div className={css.head} {...part('identity')}>
          <div className={css.who}>
            {state.avatarUrl ? <img className={css.av} src={state.avatarUrl} alt="" /> : <span className={css.av} aria-hidden="true">{state.initials}</span>}
            <div>
              <div className={`${account.eyebrow} ${css.kicker}`}>Profile</div>
              <h1 className={css.h1}>{state.name}</h1>
              <p className={css.meta}>{state.meta}</p>
            </div>
          </div>
          <div className={css.sess}>
            <b>{state.sessions}</b>
            <span>Sessions</span>
          </div>
        </div>

        <section className={css.sec} aria-label="My info" {...part('myInfo')}>
          <h2 className={css.h2}>My info</h2>
          <MyInfo {...runtime} />
        </section>

        <section className={css.sec} aria-label="Payment methods" {...part('cards')}>
          <h2 className={css.h2}>Payment methods</h2>
          <Cards {...runtime} />
        </section>

        <section className={css.sec} aria-label="Preferences">
          <h2 className={css.h2}>Preferences</h2>
          <div className={css.set} {...part('timezone')}>
            <span className={css.k}>Time zone</span>
            <span className={css.v}>
              {state.timezone}
              <span className={css.by}> · Set by this device</span>
            </span>
          </div>
        </section>

        <div className={css.foot} {...part('legal')}>
          <div className={css.legal}>
            <a href={state.privacyHref} target="_blank" rel="noopener noreferrer">
              Privacy
            </a>
            <span aria-hidden="true">·</span>
            <a href={state.termsHref} target="_blank" rel="noopener noreferrer">
              Terms
            </a>
          </div>
          <div className={css.brandLine}>
            Powered by <b>Telify</b>
          </div>
          <div className={css.ver}>{state.version}</div>
        </div>
      </div>

      <RemoveWindow {...runtime} />
      {state.saved && (
        <div className={account.toast} role="status" {...part('saved')}>
          Your info is saved.
        </div>
      )}
    </div>
  );
}
